import React, { useState } from 'react';
import LoadCountries from '../../request/Countries/LoadCountries';
import LoadCurrencies from '../../request/Countries/LoadCurrencies';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Chip,
  Alert,
  LinearProgress,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PersonAdd,
  Business,
  Language,
  Payment
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2PPartnerApplication = ({ onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Load countries and currencies data
  const { data: countriesData } = LoadCountries();
  const { data: currenciesData } = LoadCurrencies();
  const countries = countriesData?.countries || [];
  const currencies = currenciesData?.currencies || [];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    partner_name: '',
    country: '',
    phone: '',
    supported_currencies: [],
    payment_methods: [],
    business_description: ''
  });

  // Available options - using currencies from database

  const paymentMethods = [
    'Bank Transfer', 'Mobile Money', 'PayPal', 'Skrill', 'Neteller',
    'Perfect Money', 'Payoneer', 'Western Union', 'MoneyGram',
    'Cash Deposit', 'Digital Wallet', 'Cryptocurrency'
  ];

  // Countries loaded from database via LoadCountries hook

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.partner_name || !formData.country) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    if (formData.supported_currencies.length === 0) {
      setError('Please select at least one supported currency.');
      setLoading(false);
      return;
    }

    if (formData.payment_methods.length === 0) {
      setError('Please select at least one payment method.');
      setLoading(false);
      return;
    }

    try {
      // Import the service dynamically to avoid circular imports
      const { default: p2pPartnerService } = await import('../../services/p2pPartner.service');
      const data = await p2pPartnerService.applyForPartnership(formData);

      if (data.success) {
        setSuccess('Application submitted successfully! We will review your application and get back to you soon.');
        setTimeout(() => {
          onSuccess && onSuccess();
        }, 2000);
      } else {
        setError(data.message || 'Failed to submit application.');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper sx={{ p: 4, mb: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
          <PersonAdd sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Become a P2P Partner
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Join our network of trusted P2P traders and earn from cryptocurrency exchanges
          </Typography>
        </Paper>

        <Card sx={{ boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
              Partner Application
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business color="primary" />
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Partner/Business Name *"
                    value={formData.partner_name}
                    onChange={(e) => handleInputChange('partner_name', e.target.value)}
                    variant="outlined"
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={countries}
                    getOptionLabel={(option) => option.name || ''}
                    value={countries.find(c => c.name === formData.country) || null}
                    onChange={(event, value) => handleInputChange('country', value?.name || '')}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter(option =>
                        option.name.toLowerCase().includes(inputValue.toLowerCase())
                      );
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Country *" 
                        required 
                        placeholder="Search countries..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img 
                          src={option.flag} 
                          alt={option.name}
                          style={{ width: 24, height: 18, borderRadius: '2px', objectFit: 'cover' }}
                        />
                        <Typography variant="body2" fontWeight={500}>
                          {option.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                          {option.code}
                        </Typography>
                      </Box>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    variant="outlined"
                  />
                </Grid>

                {/* Supported Currencies */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Language color="primary" />
                    Supported Currencies
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={currencies}
                    getOptionLabel={(option) => option.currency_code || ''}
                    value={currencies.filter(c => formData.supported_currencies.includes(c.currency_code))}
                    onChange={(event, value) => {
                      // Get unique currency codes to prevent duplicates
                      const uniqueCurrencyCodes = [...new Set(value.map(v => v.currency_code))];
                      handleInputChange('supported_currencies', uniqueCurrencyCodes);
                    }}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter(option =>
                        option.currency_code.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.currency.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.country_name.toLowerCase().includes(inputValue.toLowerCase())
                      );
                    }}
                    isOptionEqualToValue={(option, value) => option.currency_code === value.currency_code}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={`${option.currency_code} (${option.currency})`}
                          {...getTagProps({ index })}
                          color="primary"
                        />
                      ))
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img 
                          src={option.flag} 
                          alt={option.country_name}
                          style={{ width: 24, height: 18, borderRadius: '2px', objectFit: 'cover' }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {option.currency_code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({option.currency})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                          {option.country_name}
                        </Typography>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select supported currencies *"
                        placeholder="Search currencies by code, symbol, or country..."
                      />
                    )}
                  />
                </Grid>

                {/* Payment Methods */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Payment color="primary" />
                    Payment Methods
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={paymentMethods}
                    value={formData.payment_methods}
                    onChange={(event, value) => handleInputChange('payment_methods', value)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          color="secondary"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select payment methods *"
                        placeholder="Choose payment methods you accept"
                      />
                    )}
                  />
                </Grid>

                {/* Business Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Business Description"
                    value={formData.business_description}
                    onChange={(e) => handleInputChange('business_description', e.target.value)}
                    variant="outlined"
                    multiline
                    rows={4}
                    placeholder="Tell us about your business, experience, and why you want to become a P2P partner..."
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 3 }}>
                    {loading && <LinearProgress sx={{ mb: 2 }} />}
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth={isMobile}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }}
                    >
                      {loading ? 'Submitting Application...' : 'Submit Application'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>

            {/* Information Box */}
            <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                What happens next?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                1. We will review your application within 24-48 hours
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                2. You may be contacted for additional verification
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                3. Once approved, you can start setting your rates and accepting orders
              </Typography>
              <Typography variant="body2" color="text.secondary">
                4. Earn competitive fees on every successful transaction
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default P2PPartnerApplication;
